import { alphabeticalCompare } from 'shared'
import RenderedCell from './components/RenderedCell'
import { GoogleSheetRecord, Header } from '../../types'
import { CIMSColumn } from 'components/CIMSTable/CIMSColumn/types'

export function useColumns(
  headers: Header[] | undefined
): CIMSColumn<GoogleSheetRecord>[] {
  return (headers ?? []).map((header) => {
    const { formattedValue, id, note } = header
    const byId = (record: GoogleSheetRecord) => record[id]
    const fallbackWidth = Math.max(formattedValue.length * 2, 12)
    const withHelp = (length: number) => `calc(${length}ch + 20px)`
    return {
      title: (
        <RenderedCell key={id} id={id}>
          {formattedValue}
        </RenderedCell>
      ),
      render: (record) => {
        const cell = byId(record)
        if (!cell) return null
        const { headerId: _, formattedValue, ...rest } = cell
        return (
          <RenderedCell key={rest.id} {...rest}>
            {formattedValue}
          </RenderedCell>
        )
      },
      help: note,
      width: {
        withoutRecords: withHelp(fallbackWidth),
        withRecords: (records) => {
          const longestRecord = records.reduce((longest, record) => {
            const current = byId(record)?.formattedValue?.length ?? 0
            if (current > longest) return current
            return longest
          }, 0)
          return withHelp(Math.max(fallbackWidth, Math.min(longestRecord, 50)))
        },
      },
      sort: {
        with: 'callback',
        onCompare: (a, b) =>
          alphabeticalCompare(byId(a), byId(b), 'formattedValue'),
      },
      filter: {
        type: 'search',
        with: 'callback',
        onFilter: (value, record) => {
          const c = byId(record)
          const lower = value.toString().toLowerCase()
          const lowerFValue = c?.formattedValue?.toLowerCase()
          const lowerLink = c?.hyperlink?.toLowerCase()
          const lowerNote = c?.note?.toLowerCase()
          return (
            ((!!lowerFValue && lower?.includes(lowerFValue)) ||
              (!!lowerLink && lower?.includes(lowerLink)) ||
              (!!lowerNote && lower?.includes(lowerNote)) ||
              lowerFValue?.includes(lower) ||
              lowerLink?.includes(lower) ||
              lowerNote?.includes(lower)) ??
            false
          )
        },
      },
    }
  })
}
